/**
 * novaDelivery — Wave 5 Nova client-side hooks.
 *
 * Wraps three Supabase surfaces:
 *   1. `nova-tag-photo` edge function       (auto-tag on upload)
 *   2. `nova-draft-delivery-email` edge fn  (draft delivery emails)
 *   3. `find_matching_projects_for_date` RPC (project match)
 *
 * All three are best-effort — a Nova failure never blocks the user's
 * primary flow. Callers surface errors on their own terms.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// ─── Feature 1: photo tagging ────────────────────────────────────

export interface TagPhotoInput {
  photo_id: string;
  image_url: string;
}

export interface TagPhotoResult {
  photo_id: string;
  status: 'tagged' | 'skipped' | 'failed';
  tags?: string[];
  scene?: string;
  colors?: string[];
  error?: string;
}

export interface TagBatchResponse {
  tagged: number;
  skipped: number;
  errors: number;
  results: TagPhotoResult[];
}

/**
 * Fire-and-forget batch tagger. Returns the raw response so a caller
 * can log per-photo failures if they care; most callers just await
 * and swallow — the client renders tags once the query invalidates.
 *
 * The edge function caps a single call at 20 photos. Callers should
 * chunk anything larger.
 */
export async function tagPhotosBatch(
  photos: TagPhotoInput[],
): Promise<TagBatchResponse | null> {
  if (photos.length === 0) return null;
  const { data, error } = await supabase.functions.invoke('nova-tag-photo', {
    body: { photos },
  });
  if (error) throw error;
  return data as TagBatchResponse;
}

// ─── Feature 2: draft delivery email ─────────────────────────────

export interface DeliveryEmailDraft {
  subject: string;
  body: string;
  tone_notes: string;
  brand_voice_configured: boolean;
}

export const useDraftDeliveryEmail = () =>
  useMutation({
    mutationFn: async (deliveryId: string): Promise<DeliveryEmailDraft> => {
      const { data, error } = await supabase.functions.invoke(
        'nova-draft-delivery-email',
        {
          body: {
            delivery_id: deliveryId,
            public_origin: window.location.origin,
          },
        },
      );
      if (error) throw error;
      if (!data || typeof data.subject !== 'string' || typeof data.body !== 'string') {
        throw new Error('Draft response missing subject or body');
      }
      return data as DeliveryEmailDraft;
    },
  });

// ─── Feature 3: project match by EXIF date ───────────────────────

export interface ProjectMatch {
  id: string;
  name: string;
  client_name: string | null;
  scheduled_date: string;
  day_diff: number;
}

/**
 * Given an ISO date string (from EXIF), ask the RPC which projects
 * — if any — are within ±1 day. Returns [] when there's no match or
 * when the input date isn't parseable.
 */
export const useNovaProjectMatch = (isoDate: string | null | undefined) =>
  useQuery({
    queryKey: ['nova-project-match', isoDate],
    queryFn: async (): Promise<ProjectMatch[]> => {
      if (!isoDate) return [];
      // Normalize to YYYY-MM-DD — the RPC only cares about the date
      // component, not the time or timezone offset.
      const day = new Date(isoDate);
      if (Number.isNaN(day.getTime())) return [];
      const dateOnly = day.toISOString().slice(0, 10);
      const { data, error } = await supabase.rpc(
        // Cast avoids a hard dep on the auto-generated types until
        // the schema regen picks up the new RPC.
        'find_matching_projects_for_date' as any,
        { p_date: dateOnly } as any,
      );
      if (error) throw error;
      return (Array.isArray(data) ? data : []) as ProjectMatch[];
    },
    enabled: !!isoDate,
    staleTime: 60_000,
    // A missed suggestion is fine; don't retry aggressively.
    retry: false,
  });

/**
 * Read `DateTimeOriginal` from the first image of a batch. Returns
 * an ISO string or null. Uses the tiny `exifr` package (~15KB gz);
 * lazy-imported so it only ships when the studio actually drops a
 * file into the standalone uploader.
 */
export async function readExifDateFromFile(file: File): Promise<string | null> {
  try {
    const exifr = await import('exifr');
    // `parse` with `{ pick: ['DateTimeOriginal'] }` is the cheapest
    // — no thumbnails, no gps, no XMP walk.
    const parsed = await exifr.parse(file, ['DateTimeOriginal']);
    const raw = parsed?.DateTimeOriginal;
    if (!raw) return null;
    if (raw instanceof Date) return raw.toISOString();
    if (typeof raw === 'string') {
      const d = new Date(raw);
      return Number.isNaN(d.getTime()) ? null : d.toISOString();
    }
    return null;
  } catch {
    // Any parse failure => no suggestion. The studio still gets a
    // working manual uploader.
    return null;
  }
}
